import { isPlatformBrowser } from '@angular/common';
import { Injectable, signal, effect, inject, PLATFORM_ID } from '@angular/core';

export type Theme = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly STORAGE_KEY = 'theme';
  private platformId = inject(PLATFORM_ID);

  theme = signal<Theme>(this.getStoredTheme());

  constructor() {
    // Persist theme changes to localStorage
    effect(() => {
      const currentTheme = this.theme();
      if (isPlatformBrowser(this.platformId)) {
        localStorage.setItem(this.STORAGE_KEY, currentTheme);
      }
      this.applyTheme();
    });
  }

  setTheme(theme: Theme): void {
    this.theme.set(theme);
  }

  toggleTheme(): void {
    this.theme.set(this.theme() === 'dark' ? 'light' : 'dark');
  }

  initializeTheme(): void {
    this.applyTheme();
  }

  private applyTheme(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const root = document.documentElement;
    const isDark = this.theme() === 'dark';
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }

  private getStoredTheme(): Theme {
    if (!isPlatformBrowser(this.platformId)) {
      return 'light';
    }
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    // Migration: 'system' or invalid values default to 'light'
    return 'light';
  }
}
